import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { useEventParticipantTypes } from '../api';
import { EventParticipant, EventParticipantType } from '../types';
import { ParticipantTypeForm } from './paricipant-type-form';

interface CreateParticipantTypeDialogProps {
  isOpen: boolean;
  eventId?: string;
  participants?: EventParticipant[];
  participantTypes?: EventParticipantType[];
  onOpenChange: (open: boolean) => void;
}

const MAX_PHONE_NUMBERS_COUNT = 50;

export function CreateParticipantTypeDialog({
  isOpen,
  onOpenChange,
  eventId,
  participantTypes,
  participants,
}: CreateParticipantTypeDialogProps) {
  const queryClient = useQueryClient();

  const {
    createParticipantTypeMutation,
    updateParticipantTypeMutation,
    deleteParticipantTypeMutation,
  } = useEventParticipantTypes(eventId, false);

  const createParticipantTypes = async (newTypes) => {
    try {
      await createParticipantTypeMutation.mutateAsync({
        types: newTypes.map((type) => ({
          value: type.value,
          phoneNumbers: type.phoneNumbers
            .map((phone) => phone.value)
            .filter(Boolean),
        })),
      });
      toast.success('Participant types updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['eventParticipantTypes'] });
      onOpenChange(false);
    } catch (error) {
      console.error(error.message);
    }
  };

  const removeParticipantTypes = async (ids: string[]) => {
    try {
      await deleteParticipantTypeMutation(ids);
      await queryClient.invalidateQueries({ queryKey: ['eventParticipants'] });
    } catch (error) {
      console.error(error.message);
    }
  };

  const updateParticipantTypes = async (updatedTypes) => {
    try {
      await updateParticipantTypeMutation.mutateAsync({
        types: updatedTypes.map((type) => ({
          id: type.id,
          value: type.value,
          phoneNumbers: type.phoneNumbers
            .map((phone) => phone.value)
            .filter(Boolean),
        })),
      });
      toast.success('Participant types updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['eventParticipantTypes'] });
      onOpenChange(false);
    } catch (error) {
      console.error(error.message);
    }
  };

  const saveParticipantType = async (data) => {
    const existingTypes = participantTypes?.map((type) => type.id);
    const newTypes = data.types.filter(
      (type) => !existingTypes.includes(type.id)
    );
    const updatedTypes = data.types.filter((type) =>
      existingTypes.includes(type.id)
    );
    const removedTypes = existingTypes.filter(
      (type) => !data.types.some((t) => t.id === type)
    );

    try {
      if (newTypes.length) {
        await createParticipantTypes(newTypes);
      }

      if (updatedTypes.length) {
        await updateParticipantTypes(updatedTypes);
      }

      if (removedTypes.length) {
        await removeParticipantTypes(removedTypes);
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  const defaultValues = useMemo(() => {
    if (!participantTypes?.length) return undefined;

    return {
      types: participantTypes?.map((type) => ({
        value: type.value,
        phoneNumbers: type.notificationNumbers.length
          ? type.notificationNumbers?.map((phone) => ({
              value: phone.phoneNumber,
            }))
          : [{ value: '' }],
        id: type.id,
      })),
    };
  }, [participantTypes]);

  return (
    <Dialog onOpenChange={onOpenChange} open={isOpen}>
      <DialogContent className="sm:max-w-[750px]">
        <DialogHeader>
          <DialogTitle>Create participant type</DialogTitle>
          <DialogDescription>
            You can add up to {MAX_PHONE_NUMBERS_COUNT} phone numbers per type.
          </DialogDescription>
        </DialogHeader>
        <ParticipantTypeForm
          onSubmit={saveParticipantType}
          defaultValues={defaultValues}
          participants={participants}
          maxPhoneNumbersCount={MAX_PHONE_NUMBERS_COUNT}
        >
          {() => (
            <DialogFooter className="mt-6 gap-4">
              <DialogClose asChild>
                <Button
                  variant="outline"
                  className="w-[130px]"
                  data-testid="cancel-button"
                >
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="submit"
                className="w-[130px]"
                data-testid="save-button"
                isLoading={
                  createParticipantTypeMutation.isPending ||
                  updateParticipantTypeMutation.isPending
                }
              >
                Save
              </Button>
            </DialogFooter>
          )}
        </ParticipantTypeForm>
      </DialogContent>
    </Dialog>
  );
}
